import {PADDING_DIRECTION_CLASSES, PADDING_INDENT_CLASSES} from './constants';

import type {PaddingDirectionType, PaddingIndentType} from './constants';
import type {PaddingType} from './typedef';

/**
 * Builds a space-separated string of BEM-style padding modifier class names
 * from a structured padding config object.
 *
 * Maps each active direction in the `padding` object to a CSS class of the
 * form `{element}_{directionClass}-{indentClass}` (e.g. `"card_pl-m"`). Only
 * directions with a truthy indent value are included in the output. Use the
 * exported `PaddingDirection` and `PaddingIndent` enums to construct the
 * config safely.
 *
 * @param element - BEM block or element name used as the class prefix (e.g. `"card"`).
 * @param padding - Map of padding directions to indent sizes. Keys are
 *   `PaddingDirection` values; values are `PaddingIndent` values.
 * @returns A space-separated string of CSS class names, or `''` if all
 *   directions are empty.
 *
 * @example
 * ```ts
 * generatePaddingClasses('card', {
 *   [PaddingDirection.LEFT]: PaddingIndent.M,
 *   [PaddingDirection.TOP]: PaddingIndent.S,
 * });
 * // → "card_pl-m card_pt-s"
 * ```
 */
export const generatePaddingClasses = (element: string, padding: PaddingType): string => (
    Object
        .keys(padding)
        .filter(item => padding[item as keyof PaddingType])
        .map(item => {
            const direction = item as PaddingDirectionType;
            const indent = padding[direction] as PaddingIndentType;

            return `${element}_${PADDING_DIRECTION_CLASSES[direction]}-${PADDING_INDENT_CLASSES[indent]}`;
        })
        .join(' ')
);
